// Resolving what someone TYPED into a combo-box question onto one of its answers.
//
// Kept separate from the widget because the matching rules are the substance: a size can be
// typed as 24, 24", 19 3/4, 19-3/4 or 19.75 and all of those have to land on the one answer
// whose label is `19-3/4"`. Getting that wrong is silent - an unmatched value leaves the
// question unanswered, and the MAPPING slot then encodes its null token, so the size simply
// vanishes from the item with no error anywhere.
//
// Normalisation goes through the encoder's OWN normalizeMixedInches, so what the box accepts
// is exactly what the encoder can turn into a token.

import { normalizeMixedInches } from "./mappingDecoder";
import { stripHtml } from "./text";

export type ComboOption = {
  /** The answer code, which is also the MAPPING token-map key. */
  code: string;
  /** The label as shown in the list, e.g. `19-3/4"`. */
  label: string;
  /** Canonical form used for matching, e.g. `19-3/4`. */
  key: string;
  /**
   * Selectable but NOT shown in the dropdown until someone uses it.
   *
   * Loading the whole NetSuite list took Flange to Nozzle End from 32 entries to 81, which
   * is a lot to scroll past for a value nobody here orders. The 49 that were not previously
   * offered are marked in questions.json (`_hiddenUntilUsed`) and matched by typing only;
   * picking one records it, and it is listed for everyone from then on.
   */
  hidden: boolean;
};

/** Constraints on what a particular question will accept.
 *
 *  `wholeNumbers` exists because not every size list is a size list in the same sense. Hood
 *  Size is whole inches only (Kevin, 2026-08-20), so `18-1/2` there is a typo, not a value —
 *  and accepting it would create a permanent NetSuite list entry for a hood nobody makes.
 *  Flange to Nozzle End genuinely has sixteenths, so it keeps the full grammar. */
export type SizeRules = { wholeNumbers?: boolean };

/** normalizeMixedInches passes non-numeric text straight through ("garbage" -> "garbage"),
 *  so a shape check is needed before trusting it. Accepts 24, 24", 19 3/4, 19-3/4, 19.75
 *  and rejects anything else. A bare fraction like 19/4 is deliberately NOT accepted: the
 *  normalizer reads it as 4-3/4, which is not what someone typing a nozzle length means.
 *
 *  With `wholeNumbers`, only a bare integer passes. `24.0` is rejected too rather than
 *  rounded: a question that takes whole inches should say so plainly instead of quietly
 *  reinterpreting what was typed. */
export function looksLikeSize(raw: string, rules: SizeRules = {}): boolean {
  const s = String(raw ?? "").replace(/"/g, "").replace(/\s+/g, " ").trim();
  if (!s) return false;
  if (rules.wholeNumbers) return /^\d+$/.test(s);
  return /^\d+(?:\.\d+)?$/.test(s) || /^\d+[\s-]\d+\/\d+$/.test(s);
}

/** The canonical matching key for a typed value or a label, or null when it is not a size. */
export function sizeKey(raw: string, rules: SizeRules = {}): string | null {
  if (!looksLikeSize(raw, rules)) return null;
  const n = normalizeMixedInches(raw, 16);
  return n == null ? null : String(n).trim();
}

/** Build the option list for a combo-box question, in the order given. */
export function comboOptions(
  answers: Array<{
    custrecord_cpqc_answer_code: string;
    custrecord_cpqc_answer_label_html?: string;
    name?: string;
    _hiddenUntilUsed?: boolean;
  }>
): ComboOption[] {
  const out: ComboOption[] = [];
  for (const a of answers) {
    const code = a.custrecord_cpqc_answer_code;
    const label = stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || code;
    const key = sizeKey(label);
    // A label that is not a size (e.g. "None") still belongs in the list; it just cannot be
    // reached by typing a number, only by picking it.
    out.push({ code, label, key: key ?? label.trim().toLowerCase(), hidden: a._hiddenUntilUsed === true });
  }
  return out;
}

export type ComboResolution =
  /** Typed text matched an existing option. */
  | { kind: "match"; option: ComboOption }
  /** Empty input: the question goes back to unanswered. */
  | { kind: "empty" }
  /** A well-formed size that is not in the list. */
  | { kind: "unlisted"; key: string; label: string }
  /** Not a size at all. */
  | { kind: "invalid"; typed: string };

/** Resolve typed text against the option list. Matching is on the canonical size key, so
 *  every way of writing the same size lands on the same answer; an exact label match is
 *  also honoured so non-size options ("None") can still be typed. */
export function resolveTyped(
  typed: string,
  options: ComboOption[],
  rules: SizeRules = {}
): ComboResolution {
  const raw = String(typed ?? "").trim();
  if (!raw) return { kind: "empty" };

  // An exact label or code match still wins, even under wholeNumbers. Those are values the
  // list already holds, so refusing to let someone type one that is already offered would be
  // perverse -- the rule is about what may be CREATED, not about re-reading the list.
  const byLabel = options.find(
    (o) => o.label.trim().toLowerCase() === raw.toLowerCase() || o.code.toLowerCase() === raw.toLowerCase()
  );
  if (byLabel) return { kind: "match", option: byLabel };

  const key = sizeKey(raw, rules);
  if (key == null) return { kind: "invalid", typed: raw };

  const byKey = options.find((o) => o.key === key);
  if (byKey) return { kind: "match", option: byKey };

  // A real size, just not one of the listed values. The label is rebuilt in the list's own
  // style (a trailing inch mark) so it reads the same as its neighbours.
  return { kind: "unlisted", key, label: `${key}"` };
}

/** The answer code a value should be stored under, given the current answers. Returns null
 *  when nothing should be selected. */
export function resolvedCode(r: ComboResolution): string | null {
  return r.kind === "match" ? r.option.code : null;
}
